const RESPONSIVE_BREAKPOINT = 992
class OptimizedCollectionFilters {
  constructor() {
    this.isLoading = false;
    this.isLoadingMore = false;
    this.debounceTimer = null;
    this.abortController = null;
    this.intersectionObserver = null;

    this.initializeElements();
    this.setupEventListeners();
    this.initializeFromURL();
    this.initInfiniteScroll();
    this.initDesktopSidebar();
  }

  initializeElements() {
    this.elements = {
      container: document.querySelector('.collection-template'),
      sidebar: document.querySelector('[data-filters]'),
      productsGrid: document.querySelector('[data-collection-products]'),
      emptyMessage: document.querySelector('[data-empty-products]'),
      loadingIndicator: document.getElementById('infinite-loader'),
      loadTrigger: document.getElementById('load-more-trigger'),
      appliedFiltersSection: document.querySelector('[data-applied-filters]'),
      appliedFiltersList: document.querySelector('[data-applied-filters-list]'),
      productCount: document.querySelector('.product-count'),
      status: document.querySelector('[data-collection-status]'),
      filterToggles: document.querySelectorAll('[data-filters-toggle]'),
      sortToggle: document.querySelector('[data-sort-toggle]'),
      sortOptions: document.querySelector('[data-sort-options]'),
      sortCurrent: document.querySelector('[data-sort-current]'),
      clearAllButton: document.querySelector('[data-clear-all-filters]'),
      loadPreviousContainer: document.getElementById('load-previous-container'),
      loadPreviousBtn: document.getElementById('load-previous-btn')
    };
  }

  /**
   * Announces a result update in the visually hidden status region. The grid
   * itself is not a live region, so this is the only thing screen readers hear
   * when filtering, sorting, or paging changes what is on screen.
   */
  announce(message) {
    if (!this.elements.status || !message) return;
    // Re-announce an identical message by clearing the region first.
    this.elements.status.textContent = '';
    requestAnimationFrame(() => {
      this.elements.status.textContent = message;
    });
  }

  announceResultCount() {
    const strings = window.themeStrings ?? {};
    const count = this.elements.productsGrid?.children.length ?? 0;
    if (count === 0) {
      this.announce(strings.noProductsFoundAdjustFilters ?? 'No products found. Try adjusting your filters.');
      return;
    }

    const label = this.elements.productCount?.textContent.trim();
    this.announce(
      label
        ? (strings.resultsFound ?? '{{ label }} found').replace('{{ label }}', label)
        : (strings.productsFound ?? '{{ count }} products found').replace('{{ count }}', count)
    );
  }

  sanitizeHTML(str) {
    const temp = document.createElement('div');
    temp.textContent = str;
    return temp.innerHTML;
  }

  setupEventListeners() {
    this.setupFilterEvents();
    this.setupSortEvents();
    this.setupUIEvents();
    this.setupTeamSearch();
    this.setupLoadPreviousButton();
  }

  setupFilterEvents() {
    const debouncedFilterChange = this.debounce(this.handleFilterChange.bind(this), 300);

    document.querySelectorAll('[data-filter] input[type="checkbox"]').forEach(input => {
      const filterContainer = input.closest('[data-filter]');

      if (filterContainer) {
        filterContainer.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          input.checked = !input.checked;
          input.setAttribute('aria-checked', input.checked ? 'true' : 'false');

          debouncedFilterChange();
        });
      }
    });

    if (this.elements.clearAllButton) {
      this.elements.clearAllButton.addEventListener('click', this.clearAllFilters.bind(this));
    }
  }

  setupSortEvents() {
    if (this.elements.sortToggle && this.elements.sortOptions) {
      this.elements.sortToggle.addEventListener('click', (e) => {
        e.preventDefault();
        const isOpen = this.elements.sortOptions.classList.contains('active');
        this.elements.sortOptions.classList.toggle('active');
        this.elements.sortToggle.setAttribute('aria-expanded', !isOpen);
      });

      document.addEventListener('click', (e) => {
        if (!this.elements.sortToggle.contains(e.target) &&
          !this.elements.sortOptions.contains(e.target)) {
          this.closeSortOptions();
        }
      });

      // Escape closes the dropdown and returns focus to its trigger.
      this.elements.sortToggle.closest('.sort-controls')?.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        this.closeSortOptions();
        this.elements.sortToggle.focus();
      });
    }

    document.querySelectorAll('input[name="sort_by"]').forEach(radio => {
      radio.addEventListener('change', () => {
        if (radio.checked) {
          this.updateSortButton(radio);
          this.handleSortChange(radio.value);
        }
      });
    });
  }

  setupUIEvents() {
    this.elements.filterToggles.forEach(toggle => {
      toggle.addEventListener('click', () => {
        if (window.innerWidth >= RESPONSIVE_BREAKPOINT) {
          if (this.elements.sidebar.classList.contains('desktop-active')) {
            this.elements.sidebar.classList.remove('desktop-active');
            this.elements.sidebar.classList.add('desktop-hidden');
          } else {
            this.elements.sidebar.classList.remove('desktop-hidden');
            this.elements.sidebar.classList.add('desktop-active');
          }
        } else {
          this.elements.sidebar?.classList.toggle('mobile-active');
        }
        this.elements.filterToggles.forEach(btn => btn?.classList.toggle('active'));
      });
    });
  }

  setupTeamSearch() {
    const teamSearchInput = document.querySelector('[data-filter-search="team"]');
    const teamFilterList = document.querySelector('[data-filterable-list="team"]');
    const emptyResultsMessage = document.querySelector('[data-empty-results="team"]');

    if (!teamSearchInput || !teamFilterList) return;

    const originalItems = Array.from(teamFilterList.querySelectorAll('.filter-item'));

    const debouncedSearch = this.debounce((searchTerm) => {
      const normalizedSearch = searchTerm.toLowerCase().trim();
      let visibleCount = 0;

      originalItems.forEach(item => {
        const filterText = item.querySelector('.filter-text');
        if (filterText) {
          const teamName = filterText.textContent.toLowerCase().trim();
          const shouldShow = normalizedSearch === '' || teamName.includes(normalizedSearch);
          item.style.display = shouldShow ? '' : 'none';
          if (shouldShow) visibleCount++;
        }
      });

      const noMatches = visibleCount === 0 && normalizedSearch !== '';

      if (emptyResultsMessage) {
        emptyResultsMessage.classList.toggle('show', noMatches);
      }

      if (normalizedSearch !== '') {
        const strings = window.themeStrings ?? {};
        this.announce(
          noMatches
            ? strings.noTeamsFoundSearch ?? 'No teams found matching your search.'
            : (strings.teamsFound ?? '{{ count }} teams found').replace('{{ count }}', visibleCount)
        );
      }
    }, 200);

    teamSearchInput.addEventListener('input', (e) => debouncedSearch(e.target.value));
    teamSearchInput.addEventListener('search', (e) => debouncedSearch(e.target.value));

    teamSearchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.target.value = '';
        debouncedSearch('');
        e.target.blur();
      }
    });
  }

  setupLoadPreviousButton() {
    if (this.elements.loadPreviousBtn) {
      this.elements.loadPreviousBtn.addEventListener('click', () => {
        this.loadPreviousProducts();
      });
    }
  }

  initDesktopSidebar() {
    if (this.elements.sidebar) {
      if (window.innerWidth >= RESPONSIVE_BREAKPOINT) {
        this.elements.sidebar.classList.remove('mobile-active', 'desktop-hidden');
        if (!this.elements.sidebar.classList.contains('desktop-active')) {
          this.elements.sidebar.classList.add('desktop-active');
        }
      } else {
        this.elements.sidebar.classList.remove('desktop-active', 'desktop-hidden', 'mobile-active');
        this.elements.filterToggles.forEach(btn => btn?.classList.remove('active'));
      }
    }

    window.addEventListener('resize', () => {
      if (this.elements.sidebar) {
        if (window.innerWidth < RESPONSIVE_BREAKPOINT) {
          this.elements.sidebar.classList.remove('desktop-active', 'desktop-hidden', 'mobile-active');
          this.elements.filterToggles.forEach(btn => btn?.classList.remove('active'));
        }
      }
    });
  }

  async handleFilterChange() {
    if (this.isLoading) return;

    try {
      this.isLoading = true;
      this.showLoading(true);

      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      const url = this.buildFilterURL();

      this.updateURL(url.toString());

      this.updateAppliedFilters();

      const response = await fetch(url.toString(), {
        signal: this.abortController.signal
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      this.updateContent(html);

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Filter error:', error);
        window.location.href = this.buildFilterURL().toString();
      }
    } finally {
      this.isLoading = false;
      this.showLoading(false);
      this.abortController = null;
    }
  }

  async handleSortChange(sortValue) {
    try {
      this.showLoading(true);

      const url = new URL(window.location);
      url.searchParams.set('sort_by', sortValue);
      url.searchParams.delete('page');

      const response = await fetch(url.toString());

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      this.updateContent(html);
      this.updateURL(url.toString());

    } catch (error) {
      console.error('Sort error:', error);
      window.location.href = url.toString();
    } finally {
      this.showLoading(false);
    }
  }

  buildFilterURL() {
    const url = new URL(window.location);
    const currentSort = url.searchParams.get('sort_by');

    Array.from(url.searchParams.keys()).forEach(key => {
      if (key.startsWith('filter.') || key === 'page') {
        url.searchParams.delete(key);
      }
    });

    const checkedInputs = document.querySelectorAll('[data-filters] input[type="checkbox"]:checked');
    let hasFilters = false;

    checkedInputs.forEach(input => {
      if (input.name && input.value) {
        url.searchParams.append(input.name, input.value);
        hasFilters = true;
      }
    });

    if (hasFilters) {
      url.searchParams.set('filter.v.availability', '1');
    }

    if (currentSort) {
      url.searchParams.set('sort_by', currentSort);
    }

    return url;
  }

  updateContent(html) {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newProductsGrid = doc.querySelector('[data-collection-products]');
      if (newProductsGrid && this.elements.productsGrid) {
        this.elements.productsGrid.innerHTML = newProductsGrid.innerHTML;
      }

      const newProductCount = doc.querySelector('.product-count');
      if (newProductCount && this.elements.productCount) {
        this.elements.productCount.textContent = newProductCount.textContent;
      }

      const newEmptyMessage = doc.querySelector('[data-empty-products]');
      let currentEmptyMessage = document.querySelector('[data-empty-products]');

      if (newEmptyMessage) {
        if (currentEmptyMessage) {
          currentEmptyMessage.outerHTML = newEmptyMessage.outerHTML;
        } else {
          this.elements.productsGrid.insertAdjacentHTML('afterend', newEmptyMessage.outerHTML);
        }
        this.elements.emptyMessage = document.querySelector('[data-empty-products]');
      } else if (currentEmptyMessage) {
        currentEmptyMessage.remove();
        this.elements.emptyMessage = null;
      }

      const newLoadPreviousContainer = doc.getElementById('load-previous-container');
      const currentLoadPreviousContainer = document.getElementById('load-previous-container');

      if (newLoadPreviousContainer) {
        if (currentLoadPreviousContainer) {
          currentLoadPreviousContainer.outerHTML = newLoadPreviousContainer.outerHTML;
        } else {
          this.elements.productsGrid.insertAdjacentHTML('beforebegin', newLoadPreviousContainer.outerHTML);
        }
        this.elements.loadPreviousContainer = document.getElementById('load-previous-container');
        this.elements.loadPreviousBtn = document.getElementById('load-previous-btn');
      } else if (currentLoadPreviousContainer) {
        currentLoadPreviousContainer.remove();
        this.elements.loadPreviousContainer = null;
        this.elements.loadPreviousBtn = null;
      }

      const newLoadTrigger = doc.getElementById('load-more-trigger');
      const currentLoadTrigger = document.getElementById('load-more-trigger');

      if (newLoadTrigger && currentLoadTrigger) {
        currentLoadTrigger.setAttribute('data-next-url', newLoadTrigger.getAttribute('data-next-url') || '');
        currentLoadTrigger.style.display = 'block';
      } else if (currentLoadTrigger) {
        currentLoadTrigger.style.display = 'none';
        if (this.intersectionObserver) {
          this.intersectionObserver.disconnect();
        }
      }

      const hasProducts = this.elements.productsGrid.children.length > 0;

      const noMoreProductsMessage = document.querySelector('.no-more-products-message');
      if (noMoreProductsMessage) {
        noMoreProductsMessage.remove();
      }

      if (!hasProducts && this.elements.loadingIndicator) {
        this.elements.loadingIndicator.style.display = 'none';
      }

      this.syncCheckboxesWithURL();

      if (hasProducts && newLoadTrigger) {
        this.initInfiniteScroll();
      }

      this.updateAppliedFilters();

      this.closeMobileSidebar();

      this.announceResultCount();

    } catch (error) {
      console.error('Error updating content:', error);
      window.location.reload();
    }
  }

  updateAppliedFilters() {
    try {
      if (!this.elements.appliedFiltersSection || !this.elements.appliedFiltersList) {
        return;
      }

      this.elements.appliedFiltersList.innerHTML = '';

      const url = new URL(window.location.href);
      const appliedFilters = [];

      for (const [key, value] of url.searchParams.entries()) {
        if (key.startsWith('filter.')) {
          if (key === 'filter.v.availability') {
            continue;
          }

          const input = document.querySelector(`input[name="${key}"][value="${value}"]`);
          if (input) {
            const filterGroup = input.closest('[data-filter]') || input.closest('.filter-item');
            if (filterGroup) {
              const filterText = filterGroup.querySelector('.filter-text')?.textContent?.trim();
              const filterLogo = filterGroup.querySelector('.filter-logo');

              if (filterText) {
                appliedFilters.push({
                  text: this.sanitizeHTML(filterText),
                  logo: filterLogo ? filterLogo.src : null,
                  key: key,
                  value: value
                });
              }
            }
          }
        }
      }

      if (appliedFilters.length > 0) {
        appliedFilters.forEach(filter => {
          const filterElement = this.createAppliedFilterElement(filter);
          this.elements.appliedFiltersList.appendChild(filterElement);
        });
        this.elements.appliedFiltersSection.style.display = 'block';
      } else {
        this.elements.appliedFiltersSection.style.display = 'none';
      }
    } catch (error) {
      console.error('Error updating applied filters display:', error);
    }
  }

  createAppliedFilterElement(filter) {
    const filterElement = document.createElement('div');
    filterElement.className = 'applied-filter-item';

    const contentDiv = document.createElement('div');
    contentDiv.className = 'applied-filter-content';

    if (filter.logo) {
      const logoImg = document.createElement('img');
      logoImg.src = filter.logo;
      logoImg.className = 'filter-logo';
      logoImg.alt = filter.text;
      logoImg.width = 16;
      logoImg.height = 16;
      contentDiv.appendChild(logoImg);
    }

    const textSpan = document.createElement('span');
    textSpan.className = 'applied-filter-text';
    textSpan.textContent = filter.text;
    contentDiv.appendChild(textSpan);

    const removeButton = document.createElement('button');
    removeButton.className = 'applied-filter-remove';
    removeButton.innerHTML = '×';
    removeButton.setAttribute('aria-label', (window.themeStrings?.removeFilter ?? 'Remove {{ filter }} filter').replace('{{ filter }}', filter.text));

    removeButton.addEventListener('click', () => {
      this.removeFilter(filter.key, filter.value);
    });

    filterElement.appendChild(contentDiv);
    filterElement.appendChild(removeButton);

    return filterElement;
  }

  async removeFilter(filterKey, filterValue) {
    try {
      const input = document.querySelector(`input[name="${filterKey}"][value="${filterValue}"]`);
      if (input) {
        input.checked = false;
        input.setAttribute('aria-checked', 'false');
      }

      const url = new URL(window.location.href);
      const params = url.searchParams.getAll(filterKey);
      url.searchParams.delete(filterKey);

      params.forEach((param) => {
        if (param !== filterValue) {
          url.searchParams.append(filterKey, param);
        }
      });

      url.searchParams.delete('page');

      const hasOtherFilters = Array.from(url.searchParams.keys()).some(key =>
        key.startsWith('filter.') && key !== 'filter.v.availability'
      );
      if (!hasOtherFilters) {
        url.searchParams.delete('filter.v.availability');
      }

      this.updateURL(url.toString());

      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      this.updateContent(html);

    } catch (error) {
      console.error('Error removing filter:', error);
      const url = this.buildFilterURL();
      window.location.href = url.toString();
    }
  }

  clearAllFilters() {
    try {
      document.querySelectorAll('[data-filters] input[type="checkbox"]').forEach(input => {
        input.checked = false;
        input.setAttribute('aria-checked', 'false');
      });

      const teamSearchInput = document.querySelector('[data-filter-search="team"]');
      if (teamSearchInput) {
        teamSearchInput.value = '';
        teamSearchInput.dispatchEvent(new Event('input'));
      }

      const baseUrl = window.location.pathname;
      const currentUrl = new URL(window.location);
      const currentSort = currentUrl.searchParams.get('sort_by');
      const currentSearch = currentUrl.searchParams.get('q');

      const params = new URLSearchParams();
      if (currentSort) params.set('sort_by', currentSort);
      if (currentSearch) params.set('q', currentSearch);

      const cleanUrl = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;

      window.location.href = cleanUrl;
    } catch (error) {
      console.error('Error clearing filters:', error);
      window.location.href = window.location.pathname;
    }
  }

  updateSortButton(radio) {
    document.querySelectorAll('.sort-item').forEach(item => {
      item.classList.remove('selected');
    });
    radio.closest('.sort-item')?.classList.add('selected');

    this.closeSortOptions();
  }

  closeSortOptions() {
    if (!this.elements.sortOptions) return;
    this.elements.sortOptions.classList.remove('active');
    this.elements.sortToggle?.setAttribute('aria-expanded', 'false');
  }

  syncCheckboxesWithURL() {
    const url = new URL(window.location.href);

    document.querySelectorAll('[data-filters] input[type="checkbox"]').forEach(input => {
      const values = url.searchParams.getAll(input.name);
      const shouldBeChecked = values.includes(input.value);

      if (input.checked !== shouldBeChecked) {
        input.checked = shouldBeChecked;
        input.setAttribute('aria-checked', shouldBeChecked ? 'true' : 'false');
      }
    });
  }

  initializeFromURL() {
    this.syncCheckboxesWithURL();

    this.updateAppliedFilters();

    const currentSort = new URL(window.location.href).searchParams.get('sort_by');
    if (currentSort) {
      const sortRadio = document.querySelector(`input[name="sort_by"][value="${currentSort}"]`);
      if (sortRadio) {
        this.updateSortButton(sortRadio);
      }
    }

    const currentPage = parseInt(new URL(window.location.href).searchParams.get('page')) || 1;
    if (this.elements.productsGrid) {
      this.elements.productsGrid.setAttribute('data-lowest-page', currentPage);
    }
  }

  showLoading(show) {
    if (this.elements.loadingIndicator) {
      this.elements.loadingIndicator.style.display = show ? 'flex' : 'none';
    }

    if (this.elements.productsGrid) {
      this.elements.productsGrid.classList.toggle('loading', show);
    }
  }

  closeMobileSidebar() {
    if (window.innerWidth < RESPONSIVE_BREAKPOINT && this.elements.sidebar) {
      this.elements.sidebar.classList.remove('mobile-active');
      this.elements.filterToggles.forEach(btn => btn?.classList.remove('active'));
    }
  }

  updateURL(newUrl) {
    if (newUrl !== window.location.href) {
      window.history.pushState({}, '', newUrl);
    }
  }

  debounce(func, wait) {
    return (...args) => {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => func.apply(this, args), wait);
    };
  }

  initInfiniteScroll() {
    this.elements.loadTrigger = document.getElementById('load-more-trigger');

    if (this.elements.loadTrigger) {
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }

      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.isLoadingMore) {
              this.loadMoreProducts();
            }
          });
        },
        {
          rootMargin: '100px',
          threshold: 0.1
        }
      );

      this.intersectionObserver.observe(this.elements.loadTrigger);
    }
  }

  async loadMoreProducts() {
    if (this.isLoadingMore) return;

    try {
      this.isLoadingMore = true;

      if (this.elements.loadingIndicator) {
        this.elements.loadingIndicator.style.display = 'flex';
      }

      const currentUrl = new URL(window.location.href);
      const currentPage = parseInt(currentUrl.searchParams.get('page')) || 1;
      const nextPage = currentPage + 1;
      currentUrl.searchParams.set('page', nextPage);

      const response = await fetch(currentUrl.toString());
      if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);

      const data = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(data, 'text/html');

      const newProducts = doc.querySelector('[data-collection-products]');
      const newLoadTrigger = doc.getElementById('load-more-trigger');

      if (newProducts && newProducts.children.length > 0) {
        const addedProducts = Array.from(newProducts.children);
        addedProducts.forEach((product) => {
          this.elements.productsGrid.appendChild(product);
        });

        this.updateURL(currentUrl.toString());

        this.announce(`${addedProducts.length} more products loaded`);

        if (newLoadTrigger) {
          this.elements.loadTrigger.style.display = 'block';
        } else {
          this.elements.loadTrigger.style.display = 'none';
          this.showNoMoreProductsMessage();
        }
      } else {
        this.elements.loadTrigger.style.display = 'none';
        this.showNoMoreProductsMessage();
      }

    } catch (error) {
      console.error('Error loading more products:', error);
    } finally {
      this.isLoadingMore = false;

      if (this.elements.loadingIndicator) {
        this.elements.loadingIndicator.style.display = 'none';
      }
    }
  }

  showNoMoreProductsMessage() {
    if (!this.elements.productsGrid || this.elements.productsGrid.children.length === 0) {
      return;
    }

    const existingMessage = document.querySelector('.no-more-products-message');
    if (existingMessage) return;

    const message = document.createElement('div');
    message.className = 'no-more-products-message';
    message.style.cssText = 'text-align: center; padding: 40px 20px; color: #666; font-size: 16px;';
    message.textContent = window.themeStrings?.endOfCollection ?? 'You\'ve reached the end of our collection.';

    if (this.elements.productsGrid) {
      this.elements.productsGrid.parentNode.appendChild(message);
      this.announce(message.textContent);
    }
  }

  async loadPreviousProducts() {
    if (this.isLoading) return;

    try {
      this.isLoading = true;

      if (this.elements.loadPreviousBtn) {
        this.elements.loadPreviousBtn.textContent = window.themeStrings?.loading ?? 'Loading...';
        this.elements.loadPreviousBtn.disabled = true;
      }

      const currentUrl = new URL(window.location.href);
      const currentPage = parseInt(currentUrl.searchParams.get('page')) || 1;

      if (currentPage <= 1) return;
      const lowestPageLoaded = parseInt(this.elements.productsGrid.getAttribute('data-lowest-page')) || currentPage;
      const pageToLoad = lowestPageLoaded - 1;

      if (pageToLoad < 1) return;

      const pageUrl = new URL(window.location.href);
      pageUrl.searchParams.set('page', pageToLoad);

      const response = await fetch(pageUrl.toString());
      if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const pageProducts = doc.querySelector('[data-collection-products]');
      if (pageProducts && pageProducts.children.length > 0) {
        const productsToAdd = Array.from(pageProducts.children);
        productsToAdd.reverse().forEach(product => {
          this.elements.productsGrid.insertBefore(product, this.elements.productsGrid.firstChild);
        });

        this.elements.productsGrid.setAttribute('data-lowest-page', pageToLoad);
      }

      if (pageToLoad <= 1) {
        if (this.elements.loadPreviousContainer) {
          this.elements.loadPreviousContainer.style.display = 'none';
        }
      }

    } catch (error) {
      console.error('Error loading previous products:', error);
    } finally {
      this.isLoading = false;

      if (this.elements.loadPreviousBtn) {
        this.elements.loadPreviousBtn.textContent = window.themeStrings?.loadPrevious ?? 'Load previous';
        this.elements.loadPreviousBtn.disabled = false;
      }
    }
  }

  cleanup() {
    if (this.abortController) {
      this.abortController.abort();
    }

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    clearTimeout(this.debounceTimer);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  try {
    window.optimizedCollectionFilters = new OptimizedCollectionFilters();
  } catch (error) {
    console.error('Failed to initialize optimized filters:', error);
    initBasicFallback();
  }

});

function initBasicFallback() {
  document.querySelectorAll('[data-filter] input[type="checkbox"]').forEach(input => {
    const filterContainer = input.closest('[data-filter]');

    if (filterContainer) {
      filterContainer.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();

        input.checked = !input.checked;
        input.setAttribute('aria-checked', input.checked ? 'true' : 'false');

        setTimeout(() => {
          const url = new URL(window.location.href);

          document.querySelectorAll('[data-filters] input[type="checkbox"]').forEach(i => {
            url.searchParams.delete(i.name);
          });

          document.querySelectorAll('[data-filters] input[type="checkbox"]:checked').forEach(i => {
            if (i.name && i.value) {
              url.searchParams.append(i.name, i.value);
            }
          });

          const hasFilters = url.searchParams.toString().includes('filter.v.');
          if (hasFilters) {
            url.searchParams.set('filter.v.availability', '1');
          }

          url.searchParams.delete('page');
          window.location.href = url.toString();
        }, 100);
      });
    }
  });
}

if (document.readyState === 'complete' || document.readyState === 'interactive') {
  setTimeout(() => {
    if (!window.optimizedCollectionFilters) {
      try {
        window.optimizedCollectionFilters = new OptimizedCollectionFilters();
      } catch (error) {
        console.error('Immediate initialization failed:', error);
        initBasicFallback();
      }
    }
  }, 100);
}

window.addEventListener('beforeunload', () => {
  if (window.optimizedCollectionFilters) {
    window.optimizedCollectionFilters.cleanup();
  }
});